import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, LogOut, Sparkles, Menu, X, GraduationCap, ListChecks } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-brand-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white shadow-md">
              <Sparkles className="w-5 h-5" />
            </div>
            <span className="font-display font-extrabold text-xl text-slate-900">SupplySprout</span>
          </Link>

          <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="navbar-search-input"
                placeholder="Search notebooks, pens, backpacks…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 rounded-full bg-slate-50 border-slate-200 focus:bg-white"
              />
            </div>
          </form>

          <nav className="hidden md:flex items-center gap-1">
            <Link to="/shop" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-indigo-600" data-testid="nav-shop">Shop</Link>
            <Link to="/wholesale" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-indigo-600" data-testid="nav-wholesale">Wholesale</Link>
            {user?.is_admin && <Link to="/admin" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-indigo-600" data-testid="nav-admin">Admin</Link>}
            <Link to="/cart" className="relative ml-2" data-testid="navbar-cart-button">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ShoppingCart className="w-5 h-5" />
              </Button>
              {count > 0 && (
                <Badge className="absolute -top-1 -right-1 bg-coral-500 text-white h-5 min-w-5 px-1 rounded-full text-[10px]" style={{ background: "#FF6B6B" }} data-testid="navbar-cart-count">{count}</Badge>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="navbar-user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-semibold flex items-center gap-1">
                      {user.name}
                      {user.is_verified_teacher && (
                        <span title="Verified teacher" className="inline-flex items-center bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1" data-testid="verified-teacher-badge">✓ Teacher</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-normal truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/orders")} data-testid="menu-my-orders"><User className="w-4 h-4 mr-2" />My Orders</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/lists")} data-testid="menu-my-lists"><ListChecks className="w-4 h-4 mr-2" />My lists</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/teacher-verification")} data-testid="menu-teacher-verify">
                    <GraduationCap className="w-4 h-4 mr-2" />{user.is_verified_teacher ? "Teacher benefits" : "Get verified"}
                  </DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin Dashboard</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login">Log in</Button></Link>
                <Link to="/register"><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="nav-register">Sign up</Button></Link>
              </>
            )}
          </nav>

          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <form onSubmit={submit}>
              <Input placeholder="Search supplies…" value={q} onChange={(e) => setQ(e.target.value)} />
            </form>
            <Link to="/shop" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Shop</Link>
            <Link to="/wholesale" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Wholesale</Link>
            <Link to="/cart" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Cart ({count})</Link>
            {user ? (
              <>
                <Link to="/orders" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">My Orders</Link>
                {user.is_admin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Admin</Link>}
                <button onClick={() => { logout(); setMobileOpen(false); }} className="block py-2 font-medium text-red-600">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Log in</Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Sign up</Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
